import React, { useState } from 'react';
import { MessageSquare, Search, BellRing } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function HowItWorks() {
  const [activeFeature, setActiveFeature] = useState('reminders');
  const features = [{
    id: 'reminders',
    title: 'Reminders',
    steps: [{
      icon: <MessageSquare className="text-[#553116]" size={24} data-id="element-62" />,
      title: 'Tell Hermo what you want to be reminded about',
      description: "School events, renewals, visa deadlines — anything you don't want to miss."
    }, {
      icon: <Search className="text-[#553116]" size={24} data-id="element-63" />,
      title: 'Hermo monitors your inbox for relevant details',
      description: 'It spots dates, deadlines, details, and commitments - even the subtle ones.'
    }, {
      icon: <BellRing className="text-[#553116]" size={24} data-id="element-64" />,
      title: 'Receive timely reminders via your preferred channel',
      description: 'WhatsApp, imessage, email digests, or calendar invites. Always on time, never overwhelming.'
    }]
  }, {
    id: 'prioritization',
    title: 'Prioritization',
    steps: [{
      icon: <MessageSquare className="text-[#553116]" size={24} data-id="element-65" />,
      title: 'Tell Hermo which emails matter most',
      description: 'Share which senders, topics, or types of emails are important to you — in simple, conversational language.'
    }, {
      icon: <Search className="text-[#553116]" size={24} data-id="element-66" />,
      title: 'Hermo watches your inbox intelligently',
      description: 'Hermo understands context, intent, and priority - without you lifting a finger.'
    }, {
      icon: <BellRing className="text-[#553116]" size={24} data-id="element-67" />,
      title: 'Important emails are automatically highlighted',
      description: 'High-priority messages are given the visibility they deserve. Everything else can wait.'
    }]
  }, {
    id: 'automation',
    title: 'Task Automation',
    steps: [{
      icon: <MessageSquare className="text-[#553116]" size={24} data-id="element-68" />,
      title: 'Tell Hermo what actions to perform',
      description: "Describe the routines you'd love to hand off - drafting replies, updating documents, or organising attachments."
    }, {
      icon: <Search className="text-[#553116]" size={24} data-id="element-69" />,
      title: 'Hermo identifies the emails that trigger those tasks',
      description: 'Our AI recognises the moments that require action and pulls out the details automatically.'
    }, {
      icon: <BellRing className="text-[#553116]" size={24} data-id="element-70" />,
      title: 'Tasks are completed for you',
      description: 'Replies are drafted, documents updated, and admin handled in the background.'
    }]
  }];
  const currentFeature = features.find(f => f.id === activeFeature);
  return <section id="how-it-works" className="py-32 px-6 md:px-12 lg:px-24 bg-[#fffefc]" data-id="element-71">
      <div className="max-w-7xl mx-auto" data-id="element-72">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-73">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-[#2D1810] mb-4" data-id="element-74">
            How to use Hermo
          </h2>
        </div>
        {/* Feature Tabs */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12" data-id="element-75">
          {features.map(feature => <button key={feature.id} onClick={() => setActiveFeature(feature.id)} className={`px-6 py-3 rounded-full font-semibold transition-all ${activeFeature === feature.id ? 'bg-[#553116] text-white shadow-lg' : 'bg-white text-[#6B5244] hover:bg-[#F5F0ED] border border-[#E8DFD8]'}`} data-id="element-76">
              {feature.title}
            </button>)}
        </div>
        {/* Steps Display */}
        <AnimatePresence mode="wait" data-id="element-77">
          <motion.div key={activeFeature} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="grid md:grid-cols-3 gap-8 relative" data-id="element-78">
            {/* Connection lines */}
            <div className="hidden md:block absolute top-1/2 left-1/4 right-1/4 h-0.5 bg-[#D4C4B8] z-0" data-id="element-79"></div>
            {currentFeature?.steps.map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: i * 0.1
          }} className="bg-white rounded-xl p-6 shadow-md border border-[#E8DFD8] relative z-10" data-id="element-80">
                <div className="w-12 h-12 bg-[#F5F0ED] rounded-full flex items-center justify-center mb-4" data-id="element-81">
                  {step.icon}
                </div>
                <h4 className="text-lg font-semibold text-[#2D1810] mb-3" data-id="element-82">
                  {step.title}
                </h4>
                <p className="text-[#6B5244]" data-id="element-83">{step.description}</p>
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-[#553116] rounded-full flex items-center justify-center text-white font-bold" data-id="element-84">
                  {i + 1}
                </div>
              </motion.div>)}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}